﻿<script lang="ts">
    import * as Leaflet from 'leaflet';
    import 'leaflet/dist/leaflet.css';
    import type { LatLngExpression } from 'leaflet';
    import { getLegendColorRGB, getLegendColorString } from '../../utils/style';
    import { createEventDispatcher } from 'svelte';
    import type {RouteEditorGate} from "../../models/route-editor-gate.model";
    import type {RouteEditorRoute} from "../../models/route-editor-route.model";
    import { getAppConfig } from '../../helpers/app-config.helper';

    const dispatch = createEventDispatcher<{
        gate: string;
        latlng: Leaflet.LatLng;
    }>();

    const activeColor: string = '#930010';

    let map: Leaflet.Map;
    
    export let center: Leaflet.LatLngExpression;
    export let gates: RouteEditorGate[];
    export let routes: RouteEditorRoute[];
    export let selectedRoute: RouteEditorRoute;
    export let selectedGate: RouteEditorGate;
    export let highlightedGate: RouteEditorGate;
    export let isAddingGate = false;
    export let gatesByName: Map<string, RouteEditorGate>;

    let zoom: number = 13;
    let routeLineLayerGroup: Leaflet.LayerGroup;
    let gateCircleLayerGroup: Leaflet.LayerGroup;

    $: if (map && center) {
        initializeMap();
    }

    $: if (map && gates && routes) {
        updateRouteLines(routes, selectedRoute);
        updateGateCircles(gates, selectedGate, highlightedGate);
    }

    $: if (map && selectedRoute) {
        if (highlightedGate) {
            flyToHighlightedGate(highlightedGate);
        } else if (selectedGate) {
            flyToGate(selectedGate);
        } else {
            flyToRoute(selectedRoute);
        }
    }

    function updateRouteLines(routesToDisplay: RouteEditorRoute[], selectedRoute: RouteEditorRoute) {
        const routeLines = routesToDisplay
            .map((route, index) => [route, getLegendColorRGB(index)] as [RouteEditorRoute, [number, number, number]])
            .filter((tuple) => tuple[0].gateNames?.length > 1)
            .map((tuple) => {
                const routeGates = tuple[0].gateNames
                    .map((gateName) => gatesByName.get(gateName))
                    .map((gate) => [gate.latitude, gate.longitude] as LatLngExpression);

                if (tuple[0].id === selectedRoute?.id) {
                    return Leaflet.polyline(routeGates, { color: getLegendColorString(tuple[1], 1), weight: 5 });
                }
                return Leaflet.polyline(routeGates, {
                    color: getLegendColorString(tuple[1], 0.3),
                    dashArray: '10, 20',
                    dashOffset: '20'
                });
            });

        const newLayerGroup = Leaflet.layerGroup(routeLines);
        newLayerGroup.addTo(map);
        if (routeLineLayerGroup) {
            map.removeLayer(routeLineLayerGroup);
        }
        routeLineLayerGroup = newLayerGroup;
    }

    function updateGateCircles(gatesToDisplay: RouteEditorGate[], selectedGate: RouteEditorGate, highlighedGate: RouteEditorGate | undefined) {
        const gateCircles = gatesToDisplay.map((gate) => {
            if (selectedGate?.name === gate.name) {
                return Leaflet.circleMarker([gate.latitude, gate.longitude] as LatLngExpression, {
                    radius: 7,
                    color: activeColor,
                    fillColor: activeColor,
                    fillOpacity: 1,
                    opacity: 1
                })
                    .bindTooltip(gate.name)
                    .addEventListener('click', () => handleGateClick(gate));
            }
            if (highlightedGate?.name === gate.name) {
                return Leaflet.circleMarker([gate.latitude, gate.longitude] as LatLngExpression, {
                    radius: 9,
                    color: activeColor,
                    fillColor: activeColor,
                    fillOpacity: 1,
                    opacity: 1
                })
                    .bindTooltip(gate.name)
                    .addEventListener('click', () => handleGateClick(gate));
            }
            return Leaflet.circleMarker([gate.latitude, gate.longitude] as LatLngExpression, {
                radius: 5,
                color: 'transparent',
                fillColor: 'black',
                fillOpacity: 0.5
            })
                .bindTooltip(gate.name)
                .addEventListener('click', () => handleGateClick(gate));
        });

        const newLayerGroup = Leaflet.layerGroup(gateCircles);
        newLayerGroup.addTo(map);
        if (gateCircleLayerGroup) {
            map.removeLayer(gateCircleLayerGroup);
        }
        gateCircleLayerGroup = newLayerGroup;
    }

    function flyToRoute(route: RouteEditorRoute) {
        const routeGates = route.gateNames
            ?.map((gateName) => gatesByName.get(gateName))
            .filter((gate) => gate !== undefined)
            .map((gate) => [gate.latitude, gate.longitude] as LatLngExpression);

        if (!routeGates || routeGates?.length === 0) {
            return;
        }

        const newBounds = Leaflet.latLngBounds(routeGates).pad(0.3);
        if (map.getBounds().contains(newBounds)) {
            return;
        }
        map.flyToBounds(newBounds, { duration: 0.5 });
    }

    function flyToHighlightedGate(gate: RouteEditorGate) {
        const newBounds: [number, number] = [gate.latitude, gate.longitude];
        if (map.getBounds().contains(newBounds)) {
            return;
        }
        map.flyTo([gate.latitude, gate.longitude], map.getZoom(), { duration: 0.5 });
    }

    function flyToGate(gate: RouteEditorGate) {
        const newBounds: [number, number] = [gate.latitude, gate.longitude];
        if (map.getBounds().contains(newBounds)) {
            return;
        }
        map.flyTo(newBounds, map.getZoom(), { duration: 0.5 });
    }

    function flyToRouteOverview(allGates: RouteEditorGate[]) {
        const newBounds = Leaflet.latLngBounds(allGates.map((gate) => [gate.latitude, gate.longitude] as LatLngExpression)).pad(
            0.1
        );
        if (map.getBounds().contains(newBounds)) {
            return;
        }
        map.flyToBounds(newBounds, { duration: 0.5 });
    }

    function mapMounted(container: HTMLElement) {
        map = createMap(container);
        addControls(map);
    }

    function handleGateClick(gate: RouteEditorGate) {
        dispatch('gate', gate.name);
    }

    function createMap(container: HTMLElement) {
        let m = Leaflet.map(container, {
            zoomSnap: 0.25, // Allow more fluent zooming: not only from one tile level to the next (factor 2), but also in between
            zoomDelta: 0.25,
            preferCanvas: true, // Drastically speeds up rendering by using canvas instead of separate DOM elements per layer,
            zoomControl: false // We add our own zoom control in the bottom left corner
        });

        const appConfig = getAppConfig();
        Leaflet.tileLayer(`https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=${appConfig.cartoApiKey}`, {
            attribution: `&copy;<a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>,
            &copy;<a href="https://carto.com/attributions" target="_blank">CARTO</a>`,
            subdomains: 'abcd',
            maxZoom: 20,
            tileSize: 512,
            zoomOffset: -1
        }).addTo(m);

        m.on('click', (e) => {
            var location = e.latlng;
            dispatch('latlng', location);
        });

        return m;
    }

    function addControls(map: Leaflet.Map) {
        Leaflet.control.zoom({ position: 'bottomleft' }).addTo(map);
    }

    function initializeMap() {
        map?.invalidateSize();
        map?.setView(center, zoom);
    }
</script>

<div class="map {isAddingGate ? 'add-gate' : ''}">
    <div use:mapMounted class="map" />
</div>

<style lang="scss">
    @import '../../assets/scss/colors';

    .map {
        width: 100%;
        height: 100%;
        position: relative;
    }

    :global(.add-gate .leaflet-container) {
        cursor: crosshair;
    }
</style>
